import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { IoMdArrowBack } from "react-icons/io";
import { hrRecordsApi } from "../../Services/hrRecordsApi";
import "../../forms/UserForm.css";
import "../hrRecords.css";

function TrainingStandardFormPage({ isSidebarExpanded, mode = "create" }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = mode === "edit";
  const [formData, setFormData] = useState({
    standard_body: "",
    display_name: "",
    ref_no_label: "",
    section_label: "",
    description: "",
    sort_order: 99,
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEdit);
  const [errors, setErrors] = useState({});
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState("success");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!isEdit || !id) return;
      try {
        setPageLoading(true);
        const response = await hrRecordsApi.listTrainingStandards({
          include_inactive: true,
        });
        const found = (response || []).find(
          (item) => String(item.id) === String(id),
        );
        if (found) {
          setFormData({
            standard_body: found.standard_body || "",
            display_name: found.display_name || "",
            ref_no_label: found.ref_no_label || "",
            section_label: found.section_label || "",
            description: found.description || "",
            sort_order: found.sort_order || 99,
            is_active: Boolean(found.is_active),
          });
        }
      } catch (error) {
        console.error("Error fetching training standard:", error);
        setSeverity("error");
        setSnackbarMessage("Failed to load training standard");
        setSnackbarOpen(true);
      } finally {
        setPageLoading(false);
      }
    };

    fetchData();
  }, [id, isEdit]);

  const validateForm = () => {
    const validationErrors = {};
    if (!formData.standard_body) {
      validationErrors.standard_body = "Standard body is required.";
    }
    if (!formData.display_name) {
      validationErrors.display_name = "Display name is required.";
    }
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSaveClick = async () => {
    if (!validateForm()) return;
    try {
      setLoading(true);
      if (isEdit) {
        await hrRecordsApi.updateTrainingStandard(id, formData);
      } else {
        await hrRecordsApi.createTrainingStandard(formData);
      }
      setSeverity("success");
      setSnackbarMessage(
        `Training standard ${isEdit ? "updated" : "added"} successfully`,
      );
      setSnackbarOpen(true);
      setTimeout(() => {
        navigate("/training/standards");
      }, 1000);
    } catch (error) {
      console.error("Error saving training standard:", error);
      setSeverity("error");
      setSnackbarMessage(
        error?.response?.data?.message || "Failed to save training standard",
      );
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prevState) => ({ ...prevState, [name]: "" }));
  };

  return (
    <>
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-form-content">
          <div className="data-form-header">
            <div className="data-form-header-content">
              <div className="data-form-heading-content-left-side">
                <Tooltip title="Back" arrow>
                  <IoMdArrowBack
                    className="back-company"
                    onClick={() => navigate("/training/standards")}
                  />
                </Tooltip>
                {isEdit ? "Edit Training Standard" : "New Training Standard"}
              </div>
            </div>
          </div>
          <div className="header-separator"></div>
          <div className="data-form-body">
            {pageLoading ? (
              <div className="loader-container">
                <div className="loader"></div>
                <div>Loading...</div>
              </div>
            ) : (
              <form className="form-detail">
                <div className="new-data-form hr-records-form-grid-two">
                  <div className="form-group">
                    <label htmlFor="standard_body" className="hr_training_labels">
                      Standard Body<span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <input
                        type="text"
                        name="standard_body"
                        value={formData.standard_body}
                        onChange={handleInputChange}
                        placeholder="Enter standard body"
                        disabled={isEdit}
                      />
                    </div>
                    {errors.standard_body && (
                      <div className="error-message">
                        {errors.standard_body}
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="display_name" className="hr_training_labels">
                      Display Name<span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <input
                        type="text"
                        name="display_name"
                        value={formData.display_name}
                        onChange={handleInputChange}
                        placeholder="Enter display name"
                      />
                    </div>
                    {errors.display_name && (
                      <div className="error-message">{errors.display_name}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="ref_no_label" className="hr_training_labels">Reference No. Label</label>
                    <div className="input-group">
                      <input
                        type="text"
                        name="ref_no_label"
                        value={formData.ref_no_label}
                        onChange={handleInputChange}
                        placeholder="Enter reference label"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="section_label" className="hr_training_labels">Section Label</label>
                    <div className="input-group">
                      <input
                        type="text"
                        name="section_label"
                        value={formData.section_label}
                        onChange={handleInputChange}
                        placeholder="Enter section label"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="sort_order" className="hr_training_labels">Sort Order</label>
                    <div className="input-group">
                      <input
                        type="number"
                        name="sort_order"
                        value={formData.sort_order}
                        onChange={handleInputChange}
                        placeholder="Enter sort order"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="description" className="hr_training_labels">Description</label>
                    <div className="input-group">
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Enter description"
                        rows={3}
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </div>
                  </div>

                  <div className="form-group hr-records-checkbox-row">
                    <label className="hr_training_labels">Active</label>
                    <div className="input-group hr-records-checkbox-control">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>
          <div className="footer">
            <div className="button-group">
              <button
                className={
                  isEdit
                    ? "data-edit-form-save-button"
                    : "data-form-save-button"
                }
                type="button"
                onClick={handleSaveClick}
                disabled={loading}
              >
                {loading ? "Saving..." : "Save"}
              </button>
              <button
                className={
                  isEdit
                    ? "data-edit-form-cancel-button"
                    : "data-form-cancel-button"
                }
                onClick={() => navigate("/training/standards")}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={() => setSnackbarOpen(false)} severity={severity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default TrainingStandardFormPage;
